import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, inject, input, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SCREENING_CONFIG_API_SERVICE_TOKEN } from '@nk/api-lib';
import { CardListComponent, ErrorAlertComponent, NkCardListData, ProgressComponent } from '@nk/ui-lib';
import { DateFormats } from '@nk/utils-lib';
import { BehaviorSubject, catchError, combineLatest, map, Observable, of, switchMap, tap } from 'rxjs';
import { ScreeningInputParamsService } from '../../services/screening-input-params.service';
import {
  EMPTY_SCREENING_ACTION_CARD_LIST_DATA,
  SCREENING_ACTION_CARD_LIST_CONFIG,
  ScreeningActionCardListItem,
  transformToScreeningActionCardListItems,
} from '../../utils/screening-action.utils';

@Component({
  selector: 'nk-screening-action-card-list',
  imports: [AsyncPipe, TranslateModule, ProgressComponent, CardListComponent, ErrorAlertComponent],
  templateUrl: './screening-action-card-list.component.html',
  styleUrls: ['./screening-action-card-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningActionCardListComponent implements OnInit {
  public screeningEntityUuid = input<string | undefined>();
  public data$!: Observable<NkCardListData<ScreeningActionCardListItem>>;
  public cardListConfig = SCREENING_ACTION_CARD_LIST_CONFIG;
  public dateFormats = DateFormats;
  public isLoading = signal(false);
  public error = signal<HttpErrorResponse | null>(null);
  private _filters$ = new BehaviorSubject<{
    page: number;
    limit: number;
  }>({ page: 0, limit: 10 });
  private _screeningConfigService = inject(SCREENING_CONFIG_API_SERVICE_TOKEN);
  private _screeningInputParamsService = inject(ScreeningInputParamsService);

  public ngOnInit(): void {
    this.isLoading.set(true);
    this.data$ = combineLatest([this._screeningInputParamsService.screeningEntitiesInputParams$, this._filters$]).pipe(
      switchMap(([params, filters]) => {
        this.error.set(null);
        const screeningConfigUuid = params?.screeningConfigUuid;
        const monitoringOriginEntityUuid = params?.monitoringOriginEntityUuid;
        const screeningEntityUuid = this.screeningEntityUuid();
        if (!screeningConfigUuid || !monitoringOriginEntityUuid || !screeningEntityUuid) {
          return of(EMPTY_SCREENING_ACTION_CARD_LIST_DATA);
        }

        const offset = filters.page * filters.limit;
        return this._screeningConfigService.screeningActionCompositeCollectionCache
          .get(
            screeningConfigUuid,
            monitoringOriginEntityUuid,
            screeningEntityUuid,
            true,
            ['screening_action.decision_timestamp:desc'],
            filters.limit,
            offset,
            false,
          )
          .pipe(
            map((screeningActionCompositeCollection) => {
              const entities = transformToScreeningActionCardListItems(
                screeningActionCompositeCollection?.elements || [],
              );
              return {
                entities,
                pagination: {
                  limit: filters.limit,
                  page: filters.page,
                  totalCount: screeningActionCompositeCollection.count,
                },
              };
            }),
            tap(() => this.isLoading.set(false)),
            catchError((error) => {
              console.error('Error fetching screening actions:', error);
              this.error.set(error);
              this.isLoading.set(false);
              return of(EMPTY_SCREENING_ACTION_CARD_LIST_DATA);
            }),
          );
      }),
    );
  }

  public onChangePage(page: number): void {
    const value = this._filters$.getValue();
    this._filters$.next({ ...value, page });
  }
}
